import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageContainer } from '../../components/PageContainer';
import { Button } from '../../components/ui/Button';
import { trpc } from '../../lib/trpc';
import {
  FEEDBACK_STATUSES,
  type FeedbackCategory,
  type FeedbackStatus,
} from '@wineryxplorer/shared';

const CATEGORY_LABEL: Record<FeedbackCategory, string> = {
  data_error: 'Data error',
  bug: 'Bug',
  feature_request: 'Feature request',
  contact_request: 'Contact request',
  other: 'Other',
};

// Tailwind classes per category. Bg + border + text — restrained palette,
// each distinguishable at a glance without competing with the primary action.
const CATEGORY_BADGE_CLASSES: Record<FeedbackCategory, string> = {
  bug: 'bg-red-50 border-red-200 text-red-800',
  data_error: 'bg-amber-50 border-amber-200 text-amber-800',
  feature_request: 'bg-primary-50 border-primary-200 text-primary-800',
  contact_request: 'bg-secondary-50 border-secondary-200 text-secondary-800',
  other: 'bg-gray-100 border-gray-300 text-gray-700',
};

const CategoryBadge = ({ category }: { category: FeedbackCategory }) => (
  <span
    className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-sans font-semibold uppercase tracking-wide ${CATEGORY_BADGE_CLASSES[category]}`}
  >
    {CATEGORY_LABEL[category]}
  </span>
);

const STATUS_LABEL: Record<FeedbackStatus, string> = {
  new: 'New',
  in_progress: 'In progress',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
};

const entityHref = (type: string | undefined, id: string | undefined) => {
  if (!type || !id) return null;
  if (type === 'venue') return `/wineries/${id}`;
  if (type === 'wine') return `/wines/${id}`;
  if (type === 'event') return `/events/${id}`;
  return null;
};

export const AdminFeedbackDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const query = trpc.feedback.getById.useQuery(
    { id: id ?? '' },
    { enabled: !!id }
  );

  const [status, setStatus] = useState<FeedbackStatus>('new');
  const [adminNote, setAdminNote] = useState('');

  useEffect(() => {
    if (query.data) {
      setStatus(query.data.status);
      setAdminNote(query.data.adminNote ?? '');
    }
  }, [query.data]);

  const mutation = trpc.feedback.updateStatus.useMutation({
    onSuccess: () => {
      utils.feedback.list.invalidate();
      utils.feedback.pendingCount.invalidate();
      utils.feedback.getById.invalidate({ id: id ?? '' });
    },
  });

  // Priority is its own one-click mutation — no Save button required.
  const priorityMutation = trpc.feedback.updatePriority.useMutation({
    onSuccess: () => {
      utils.feedback.list.invalidate();
      utils.feedback.getById.invalidate({ id: id ?? '' });
    },
  });

  if (!id) {
    return (
      <div>
        <PageContainer>
          <p className="text-error">Missing feedback id.</p>
        </PageContainer>
      </div>
    );
  }

  if (query.isLoading) {
    return (
      <div>
        <PageContainer>
          <p className="text-gray-500">Loading…</p>
        </PageContainer>
      </div>
    );
  }

  if (query.error) {
    return (
      <div>
        <PageContainer>
          <p className="text-error">{query.error.message}</p>
        </PageContainer>
      </div>
    );
  }

  const fb = query.data;
  if (!fb) return null;

  const submitter = fb.submittedBy;
  const submitterLine = submitter
    ? `${submitter.userName ?? '—'}${submitter.email ? ` (${submitter.email})` : ''}`
    : '—';
  const resolver = fb.resolvedBy;
  const resolverLine = resolver
    ? `${resolver.userName ?? '—'}${resolver.email ? ` (${resolver.email})` : ''}`
    : null;

  const onSave = () => {
    if (mutation.isPending) return;
    mutation.mutate({
      id: fb._id,
      status,
      adminNote, // empty string intentionally clears the note
    });
  };

  const href = entityHref(fb.relatedEntityType, fb.relatedEntityId);

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Feedback detail</h1>
          <div className="mt-2">
            <CategoryBadge category={fb.category} />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="primary"
            onClick={onSave}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
          <button
            type="button"
            onClick={() => navigate('/admin/feedback')}
            className="text-sm text-gray-600 hover:text-primary-700 underline-offset-2 hover:underline"
          >
            Back to list
          </button>
        </div>
      </div>

      <PageContainer>
        <dl className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-y-4 gap-x-6 text-sm">
          <dt className="font-semibold text-gray-700">Submitted</dt>
          <dd className="text-gray-900">{new Date(fb.createdAt).toLocaleString()}</dd>

          <dt className="font-semibold text-gray-700">From</dt>
          <dd className="text-gray-900">{submitterLine}</dd>

          <dt className="font-semibold text-gray-700">Related</dt>
          <dd className="text-gray-900">
            {fb.relatedEntityName ? (
              href ? (
                <a href={href} target="_blank" rel="noreferrer" className="text-primary-700 underline">
                  {fb.relatedEntityType}: {fb.relatedEntityName}
                </a>
              ) : (
                <>
                  {fb.relatedEntityType}: {fb.relatedEntityName}
                </>
              )
            ) : (
              '—'
            )}
          </dd>

          <dt className="font-semibold text-gray-700">Page URL</dt>
          <dd className="text-gray-900 break-all">
            {fb.pageUrl ? (
              <a href={fb.pageUrl} target="_blank" rel="noreferrer" className="text-primary-700 underline">
                {fb.pageUrl}
              </a>
            ) : (
              '—'
            )}
          </dd>

          <dt className="font-semibold text-gray-700">Message</dt>
          <dd className="text-gray-900 whitespace-pre-wrap">{fb.message}</dd>

          <dt className="font-semibold text-gray-700">Last updated</dt>
          <dd className="text-gray-900">{new Date(fb.updatedAt).toLocaleString()}</dd>

          {fb.resolvedAt && (
            <>
              <dt className="font-semibold text-gray-700">Resolved at</dt>
              <dd className="text-gray-900">{new Date(fb.resolvedAt).toLocaleString()}</dd>
            </>
          )}
          {resolverLine && (
            <>
              <dt className="font-semibold text-gray-700">Resolved by</dt>
              <dd className="text-gray-900">{resolverLine}</dd>
            </>
          )}
        </dl>

        <hr className="my-6 border-gray-200" />

        <div className="grid gap-5 max-w-2xl">
          <div>
            <label htmlFor="detail-status" className="block text-sm font-semibold text-gray-900 mb-1">
              Status
            </label>
            <select
              id="detail-status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as FeedbackStatus);
              }}
              className="block w-full max-w-xs rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-base focus:border-primary-700 focus:outline-none focus:ring-1 focus:ring-primary-700"
            >
              {FEEDBACK_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="detail-priority" className="block text-sm font-semibold text-gray-900 mb-1">
              Priority
            </label>
            <select
              id="detail-priority"
              value={fb.priority ?? ''}
              disabled={priorityMutation.isPending}
              onChange={(e) =>
                priorityMutation.mutate({
                  id: fb._id,
                  priority: e.target.value === '' ? null : Number(e.target.value),
                })
              }
              className="block w-full max-w-xs rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-base focus:border-primary-700 focus:outline-none focus:ring-1 focus:ring-primary-700"
            >
              <option value="">— (unset)</option>
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              1 = lowest, 5 = highest. Saves immediately.
            </p>
            {priorityMutation.error ? (
              <p className="mt-1 text-xs text-error">{priorityMutation.error.message}</p>
            ) : null}
          </div>

          <div>
            <label htmlFor="detail-note" className="block text-sm font-semibold text-gray-900 mb-1">
              Admin note
            </label>
            <textarea
              id="detail-note"
              value={adminNote}
              onChange={(e) => {
                setAdminNote(e.target.value);
              }}
              rows={6}
              maxLength={2000}
              placeholder="Notes for the team. Leave blank to clear an existing note."
              className="block w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-base focus:border-primary-700 focus:outline-none focus:ring-1 focus:ring-primary-700"
            />
            <div className="mt-1 text-xs text-gray-500">
              {adminNote.length} / 2000 characters
            </div>
          </div>

          {mutation.error && (
            <p className="text-sm text-error">{mutation.error.message}</p>
          )}
        </div>
      </PageContainer>
    </div>
  );
};
